import * as React from "react";
import { useState } from "react";
import LoginIcon from "@mui/icons-material/Login";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LanguageIcon from "@mui/icons-material/Language";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Swal from "sweetalert2";
import logo from "../image/meesok.png";
import "./login.css";
import { useLanguage } from "../common/i18n/LanguageContext";

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#f8fafc",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#479cff" },
    "&.Mui-focused fieldset": { borderColor: "#0f62e6", borderWidth: "2px" },
  },
};

export default function SignIn() {
  const { lang, setLang, t } = useLanguage();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const json = {
      Username: data.get("username"),
      Password: data.get("password"),
    };
    setLoading(true);
    fetch(`${process.env.REACT_APP_API_KEY}/adminlogin`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(json),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.resCode === 200) {
          localStorage.setItem("token", data.data.token);
          localStorage.setItem(
            "permissions",
            JSON.stringify(data.data.permissions),
          );
          // Send the user back to whatever page they were on when their
          // token expired (saved by ApiConfig's 401 handler), instead of
          // always dropping them on the dashboard.
          const redirectTo = sessionStorage.getItem("postLoginRedirect");
          sessionStorage.removeItem("postLoginRedirect");
          window.location = redirectTo || "/";
        } else if (data.message === "suspend") {
          Swal.fire({
            icon: "warning",
            title: t("login_suspended_title"),
            text: t("login_suspended_text"),
            confirmButtonColor: "#FFDE1",
            confirmButtonText: t("common_ok"),
          });
        } else {
          Swal.fire({
            icon: "error",
            title: t("login_error_title"),
            text: t("login_error_text"),
            confirmButtonColor: "#d33",
            confirmButtonText: t("common_ok"),
          });
        }
        // console.log('Success:', data);
      })
      .catch((error) => {
        // console.error('Error:', error);
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="loginbg min-h-screen w-full flex items-center justify-center bg-brand-blue-900 p-4">
      <div className="w-full max-w-[420px] bg-white rounded-2xl shadow-2xl p-8">
        <div className="flex justify-end items-center gap-1 mb-2">
          <LanguageIcon className="text-slate-400" sx={{ fontSize: 14 }} />
          <button
            type="button"
            onClick={() => setLang("th")}
            className={`text-[11px] font-bold px-1 ${
              lang === "th"
                ? "text-brand-blue-700 underline"
                : "text-slate-400 hover:text-brand-blue-700"
            }`}
          >
            ไทย
          </button>
          <span className="text-slate-300 text-[11px]">|</span>
          <button
            type="button"
            onClick={() => setLang("en")}
            className={`text-[11px] font-bold px-1 ${
              lang === "en"
                ? "text-brand-blue-700 underline"
                : "text-slate-400 hover:text-brand-blue-700"
            }`}
          >
            EN
          </button>
          <span className="text-slate-300 text-[11px]">|</span>
          <button
            type="button"
            onClick={() => setLang("lo")}
            className={`text-[11px] font-bold px-1 ${
              lang === "lo"
                ? "text-brand-blue-700 underline"
                : "text-slate-400 hover:text-brand-blue-700"
            }`}
          >
            ລາວ
          </button>
          <span className="text-slate-300 text-[11px]">|</span>
          <button
            type="button"
            onClick={() => setLang("zh")}
            className={`text-[11px] font-bold px-1 ${
              lang === "zh"
                ? "text-brand-blue-700 underline"
                : "text-slate-400 hover:text-brand-blue-700"
            }`}
          >
            中文
          </button>
          <span className="text-slate-300 text-[11px]">|</span>
          <button
            type="button"
            onClick={() => setLang("vi")}
            className={`text-[11px] font-bold px-1 ${
              lang === "vi"
                ? "text-brand-blue-700 underline"
                : "text-slate-400 hover:text-brand-blue-700"
            }`}
          >
            VI
          </button>
        </div>
        <div className="flex flex-col items-center mb-6">
          <img src={logo} alt="Meesok" className="w-44 mb-1" />
          <h1 className="text-xl font-bold text-slate-800">
            {t("login_title")}
          </h1>
          <p className="text-sm text-slate-400 mt-1 text-center">
            {t("login_subtitle")}
          </p>
        </div>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            margin="normal"
            size="small"
            required
            fullWidth
            id="username"
            name="username"
            label={t("login_username_label")}
            autoFocus
            sx={textFieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineIcon className="text-slate-400" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            margin="normal"
            size="small"
            required
            fullWidth
            name="password"
            label={t("login_password_label")}
            type={showPassword ? "text" : "password"}
            id="password"
            autoComplete="current-password"
            sx={textFieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon className="text-slate-400" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                    size="small"
                    aria-label={
                      showPassword
                        ? t("login_hide_password")
                        : t("login_show_password")
                    }
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-b from-brand-gold-300 via-brand-gold-400 to-brand-gold-600 text-brand-ink-onGold border border-brand-gold-200 shadow-gold hover:brightness-105 disabled:opacity-60 disabled:cursor-not-allowed font-bold py-2.5 rounded-lg mt-4 transition-colors"
          >
            {loading ? (
              t("login_signing_in")
            ) : (
              <>
                <LoginIcon fontSize="small" />
                {t("login_title")}
              </>
            )}
          </button>
        </Box>
      </div>
    </div>
  );
}
